import { Moment } from 'moment';
import {
  LoopbackFilter,
  loopbackGet,
  loopbackPost,
} from '..';
import {
  Trial,
  TrialCategory,
  Survey,
  GroundTruth,
  Enrollment,
  ThirdPartyTrial,
  DigitalSurvey,
} from '../../redux/types';

import { constructQueryParams } from './apiUtil';
import { EvaluationSheet } from '../../components/utilities/ExcelExport';

type TrialWithSurveyAndGT = Trial & {
  thirdPartyTrial?: unknown, survey?: Survey & { groundTruth?: GroundTruth[] };
}

type TrialWithSurvey = Trial & {
  survey?: Survey;
  digitalSurveys?: DigitalSurvey[];
}

type TrialExtended = Trial & { thirdPartyTrial?: ThirdPartyTrial };

export const getTrialCategories = (): Promise<TrialCategory[]> => loopbackGet('TrialCategories/getTrialCategories');

export const searchTrials = (
  filter: any, query: string, locale: string,
): Promise<TrialExtended[]> => {
  const queryParams = constructQueryParams(filter, query, locale);
  return loopbackGet(`trials/search${queryParams}`);
};

/**
* Fetching trials list as constructed in a <LoopbackProvider> in TrialsView.js.
* This Api is declared in APIComponent to support redux friendly development.
* This call may only be accessable to admin.
*
* @param {Moment} startDateISO - date ISO format
* @param {Moment} endDateISO - date in ISO format
* @returns Promise<Response> - Promise with List of trials.
* Response contain also survey and groundtruths
*/
export const getTrialsList = (
  startDateISO: Moment, endDateISO: Moment, locale: string,
): Promise<TrialExtended[]> => {
  const filter = {
    where: {
      startDate: {
        between: [
          startDateISO,
          endDateISO,
        ],
      },
    },
  };
  const queryParams = constructQueryParams(filter, undefined, locale);
  const uri = `trials/search${queryParams}`;
  return loopbackGet(uri);
};

export const getTrials = <T extends Trial>(filter?: LoopbackFilter): Promise<T[]> => {
  const queryParams = constructQueryParams(filter);
  return loopbackGet(`trials${queryParams}`);
};

/*
* This async action uses trials/search end-point which returns trial object
*/
export const getTrial = async (trialId: number, locale = '*'): Promise<Trial> => {
  const filter = {
    where: { id: trialId },
  };
  const promise = new Promise<Trial>((onSuccess, onError) => {
    loopbackGet(`Trials/search?filter=${JSON.stringify(filter)}&locale=${locale}`).then((trials: Trial[]) => {
      onSuccess(trials[0]);
    }, onError);
  });

  return promise;
};

export const getTrialWithSurvey = async (trialId: number, locale = '*'): Promise<TrialWithSurvey> => {
  const uri = `Trials/${trialId}/withSurvey?locale=${locale}`;
  return loopbackGet(uri);
};

export const getTrialWithSurveyAndGroundTruth = async (trialId: number, locale = '*'): Promise<TrialWithSurveyAndGT> => {
  const uri = `Trials/${trialId}/withSurveyAndGroundTruth?locale=${locale}`;
  return loopbackGet(uri);
};

export const enrollToTrial = (trial: Trial): Promise<Enrollment> => {
  const uri = `trials/${trial.id}/enroll`;
  return loopbackPost(uri);
};

/** restricted to admin only */
export const enrollUserToTrial = (
  trialId: number,
  userId: number,
  enrollmentRole: string,
): Promise<Enrollment> => {
  const uri = `trials/${trialId}/enrollUser?userId=${userId}&enrollmentRole=${enrollmentRole}`;
  return loopbackGet(uri);
};

export const changeEnrollmentRole = (
  trialId: number,
  userId: number,
  enrollmentRole: string,
): Promise<Enrollment> => {
  const enrollmentRoleQuery = enrollmentRole ? `&enrollmentRole=${enrollmentRole}` : '';
  const uri = `trials/${trialId}/changeEnrollmentRole?userId=${userId}${enrollmentRoleQuery}`;
  return loopbackGet(uri);
};

export type BulkEnrollResult = {
  enrolled: number;
  failed: number;
  skipped?: number;
  errors: { userId: number; error: string }[];
};

export const bulkEnrollUsers = (
  trialId: number,
  userIds: number[],
  enrollmentRole = 'none',
): Promise<BulkEnrollResult> => {
  const uri = `trials/${trialId}/bulkEnrollUsers?enrollmentRole=${encodeURIComponent(enrollmentRole)}`;
  return loopbackPost(uri, userIds);
};

export type NotifyRegisteredUsersResult = {
  dryRun: boolean;
  send: boolean;
  trialId: number;
  items: Array<{
    enrollmentId: number;
    userId: number;
    locale: string;
    recipients: string[];
    singleRecipientOnly: boolean;
    subject: string;
    html: string;
  }>;
  skipped: Array<{ enrollmentId: number; reason: string }>;
};

export const notifyRegisteredUsers = (
  trialId: number,
  enrollmentIds: number[],
): Promise<NotifyRegisteredUsersResult> => {
  const uri = `trials/${trialId}/notifyRegisteredUsers`;
  return loopbackPost(uri, { enrollmentIds });
};

export const getTrialsV2 = (): Promise<(Trial & { digitalSurveys: DigitalSurvey[] })[]> => loopbackGet('trials/getTrialsV2');

export const exportEvaluations = (trial: Trial): Promise<EvaluationSheet> => loopbackPost(`trials/${trial.id}/exportEvaluationList`);

export const exportSubmissions = (trial: Trial): Promise<EvaluationSheet> => loopbackGet(`trials/${trial.id}/exportSubmissions`);

export const getTrialById = (trialId: number): Promise<Trial> => loopbackGet(`Trials/${trialId}`);
